import type { IntlShape } from "react-intl";

import type { SelectionDataPipeline, SelectionStep } from "@/schemas/routing/selection-pipeline";

import type { FilterStep, RuleStep, SortStep } from "../types";

export const filterNames = Object.freeze({
	byReleasedForProductionDate: "FilterByReleasedForProductionDate",
	byOptimalProduction: "FilterByOptimalProduction",
	byPackStation: "FilterByPackStation",
	byStoppedCartonPropertyGroup: "FilterByStoppedCartonPropertyGroup",
	byStoppedClassification: "FilterByStoppedClassification",
	byStoppedPickZone: "FilterByStoppedPickZone",
});

export const sortNames = Object.freeze({
	byCartonPropertyGroup: "SortByCartonPropertyGroup",
	byClassification: "SortByClassification",
	byCreateByDate: "SortByCreateByDate",
	byImportDate: "SortByImportDateAscending",
	byMachineGroupExclusivity: "SortByMachineGroupExclusivity",
	byMachineOptimalProductionExclusivity: "SortByMachineOptimalProductionExclusivity",
	byPriorityAscending: "SortByPriority",
	byPriorityDescending: "SortByPriorityDescending",
	byReleasedForProductionDateAscending: "SortByReleasedForProductionDateAscending",
	byCartonPropertyGroupRatio: "SortByCartonPropertyGroupRatio",
	byGroupIdDate: "SortByGroupIdDate",
});

export const ruleNames = Object.freeze({
	pauseBetweenJobs: "PauseBetweenJobs",
	allowDistribution: "AllowDistribution",
	tileAcrossBatches: "TileAcrossBatches",
	tileWithinBatches: "TileWithinBatches",
	failAfterRetries: "FailAfterRetries",
	produceOddTilingPair: "ProduceOddTilingPair",
	produceByWaveId: "ProduceByWaveId",
});

/**
 * Returns a fresh catalog of filter steps every call - `sortListByConfiguration` mutates
 * the array it is given via splice, so these factories must not be memoized across renders.
 */
export const getFilterSteps = (intl: IntlShape): FilterStep[] => [
	{
		label: intl.formatMessage({ id: "By Released For Production", defaultMessage: "By Released For Production" }),
		name: filterNames.byReleasedForProductionDate,
	},
	{
		label: intl.formatMessage({ id: "By Optimal Production", defaultMessage: "By Optimal Production" }),
		name: filterNames.byOptimalProduction,
	},
	{
		label: intl.formatMessage({ id: "By Pack Station", defaultMessage: "By Pack Station" }),
		name: filterNames.byPackStation,
	},
	{
		label: intl.formatMessage({
			id: "By Stopped Carton Property Group",
			defaultMessage: "By Stopped Carton Property Group",
		}),
		name: filterNames.byStoppedCartonPropertyGroup,
	},
	{
		label: intl.formatMessage({ id: "By Stopped Classification", defaultMessage: "By Stopped Classification" }),
		name: filterNames.byStoppedClassification,
	},
	{
		label: intl.formatMessage({ id: "By Stopped Pick Zone", defaultMessage: "By Stopped Pick Zone" }),
		name: filterNames.byStoppedPickZone,
	},
];

export const getSortSteps = (intl: IntlShape): SortStep[] => [
	{
		label: intl.formatMessage({
			id: "By Carton Property Group Ratio",
			defaultMessage: "By Carton Property Group Ratio",
		}),
		name: sortNames.byCartonPropertyGroupRatio,
	},
	{
		label: intl.formatMessage({
			id: "By Carton Property Group Status",
			defaultMessage: "By Carton Property Group Status",
		}),
		name: sortNames.byCartonPropertyGroup,
	},
	{
		label: intl.formatMessage({ id: "By Classification Status", defaultMessage: "By Classification Status" }),
		name: sortNames.byClassification,
	},
	{
		label: intl.formatMessage({ id: "By Create By Date", defaultMessage: "By Create By Date" }),
		name: sortNames.byCreateByDate,
	},
	{
		label: intl.formatMessage({ id: "By Import Date", defaultMessage: "By Import Date" }),
		name: sortNames.byImportDate,
	},
	{
		label: intl.formatMessage({ id: "By Machine Group Exclusivity", defaultMessage: "By Machine Group Exclusivity" }),
		name: sortNames.byMachineGroupExclusivity,
	},
	{
		label: intl.formatMessage({
			id: "By Optimal Production Exclusivity",
			defaultMessage: "By Optimal Production Exclusivity",
		}),
		name: sortNames.byMachineOptimalProductionExclusivity,
	},
	{
		label: intl.formatMessage({ id: "By Priority Ascending", defaultMessage: "By Priority Ascending" }),
		name: sortNames.byPriorityAscending,
		conflictsWith: [sortNames.byPriorityDescending],
		configuration: { SortAscending: true },
	},
	{
		label: intl.formatMessage({ id: "By Priority Descending", defaultMessage: "By Priority Descending" }),
		name: sortNames.byPriorityDescending,
		conflictsWith: [sortNames.byPriorityAscending],
		configuration: { SortAscending: false },
	},
	{
		label: intl.formatMessage({
			id: "By Released For Production Date",
			defaultMessage: "By Released For Production Date",
		}),
		name: sortNames.byReleasedForProductionDateAscending,
	},
	{
		label: intl.formatMessage({ id: "By Group Id Date Time", defaultMessage: "By Group Id Date Time" }),
		name: sortNames.byGroupIdDate,
	},
];

export interface GetRuleStepsParams {
	intl: IntlShape;
	onTilingChanged: (checked: boolean, pipeline: SelectionDataPipeline) => SelectionDataPipeline;
}

export const getRuleSteps = ({ intl, onTilingChanged }: GetRuleStepsParams): RuleStep[] => [
	{
		label: intl.formatMessage({ id: "Pause between jobs", defaultMessage: "Pause between jobs" }),
		name: ruleNames.pauseBetweenJobs,
	},
	{
		label: intl.formatMessage({
			id: "Allow distribution of work across machine groups",
			defaultMessage: "Allow distribution of work across machine groups",
		}),
		name: ruleNames.allowDistribution,
	},
	{
		label: intl.formatMessage({
			id: "Cancel Production After 3 Failed Attempts",
			defaultMessage: "Cancel Production After 3 Failed Attempts",
		}),
		name: ruleNames.failAfterRetries,
	},
	{
		label: intl.formatMessage({ id: "Tile Across Batches", defaultMessage: "Tile Across Batches" }),
		name: ruleNames.tileAcrossBatches,
		conflictsWith: [ruleNames.tileWithinBatches],
		postSelection: onTilingChanged,
	},
	{
		label: intl.formatMessage({ id: "Tile Within Batches", defaultMessage: "Tile Within Batches" }),
		name: ruleNames.tileWithinBatches,
		conflictsWith: [ruleNames.tileAcrossBatches],
		postSelection: onTilingChanged,
	},
	{
		label: intl.formatMessage({
			id: "Tile when single cannot be produced (this will produce an additional carton for odd quantities)",
			defaultMessage: "Tile when single cannot be produced (this will produce an additional carton for odd quantities)",
		}),
		name: ruleNames.produceOddTilingPair,
		preRequesites: [ruleNames.tileAcrossBatches, ruleNames.tileWithinBatches],
	},
	{
		label: intl.formatMessage({ id: "Produce by Wave Id", defaultMessage: "Produce by Wave Id" }),
		name: ruleNames.produceByWaveId,
	},
];

/**
 * Sorts `list` so that configured steps come first, in the order they appear in `configuredSteps`,
 * followed by the remaining unconfigured steps. Mutates `list` via splice (matches original behavior).
 */
export const sortListByConfiguration = <T extends { name: string }>(
	list: T[],
	configuredSteps: SelectionStep[],
): T[] => {
	if (!configuredSteps || configuredSteps.length === 0) return list;

	const selected: T[] = [];
	configuredSteps.forEach((element) => {
		const found = list.find((item) => item.name === element.name);
		if (found) {
			const index = list.indexOf(found);
			list.splice(index, 1);
			selected.push(found);
		}
	});

	return selected.concat(list);
};
